import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { exhibits } from '@/data';
import NumberPad from '@/components/NumberPad';
import ExhibitCard from '@/components/ExhibitCard';
import FooterLogo from '@/components/FooterLogo';
import { QrCode, MapPin } from 'lucide-react';

const ExhibitAccess = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'keypad';
  const navigate = useNavigate();
  
  // Add a function to simulate QR code scanning (for demo purposes)
  const simulateScan = (exhibitId: number) => {
    navigate(`/exhibit/${exhibitId}`, { 
      state: { from: 'qr' } 
    });
  };
  
  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8 relative">
      {/* Abstract shapes in background */}
      <div className="agam-shape agam-shape-1"></div>
      <div className="agam-shape agam-shape-2"></div>
      
      {/* Page Title */}
      <h1 className="section-title mb-6 after:hidden font-bold text-center">
        {mode === 'keypad' && t('enter_exhibit_number')}
        {mode === 'qr' && t('scan_qr')}
        {mode === 'map' && t('view_map')}
        {mode === 'list' && t('browse_exhibits')}
      </h1>
      
      {/* Content based on mode */}
      {mode === 'keypad' && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <NumberPad />
        </div>
      )}
      
      {mode === 'qr' && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-br from-museum-shapes-blue to-museum-shapes-purple opacity-20 animate-pulse rounded-full"></div>
              <QrCode className="h-16 w-16 mx-auto mb-4 text-museum-shapes-blue relative z-10" />
            </div>
            <p className="text-gray-600 font-medium">
              {t('scan_qr')}
              <br />
              <span className="text-sm">(Placeholder for QR scanner)</span>
            </p>
            {/* Add a demo button that simulates scanning a QR code */}
            <div className="mt-4">
              <button
                onClick={() => simulateScan(17)} // Simulate scanning exhibit with ID 17
                className="text-sm text-museum underline"
              >
                {language === 'en' ? 'Simulate QR Scan (Demo)' : 'הדמה של סריקת QR (הדגמה)'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {mode === 'map' && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-br from-museum-shapes-green to-museum-shapes-blue opacity-20 animate-pulse rounded-full"></div>
              <MapPin className="h-16 w-16 mx-auto mb-4 text-museum-shapes-green relative z-10" />
            </div>
            <p className="text-gray-600 font-medium">
              {t('view_map')}
              <br />
              <span className="text-sm">(Placeholder for museum map)</span>
            </p>
          </div>
        </div>
      )}
      
      {mode === 'list' && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {exhibits.map(exhibit => (
              <ExhibitCard key={exhibit.id} exhibit={exhibit} />
            ))}
          </div>
        </div>
      )}
      
      <FooterLogo />
    </div>
  );
};

export default ExhibitAccess;
